import React, { useCallback, useEffect, useRef, useState } from "react";
import { type TableProps, Form, notification, Modal, Progress } from "antd";
import dayjs from 'dayjs';
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { debounce } from "lodash";
import useSWR from "swr";
import { useLocation } from "react-router-dom";

function StartLicense(stimulsoftInstance: any) {
    const key = process.env.REACT_APP_STIMULSOFT_LICENSE_KEY;
    if (key && key.trim() !== "") {
        try {
            stimulsoftInstance.Base.StiLicense.key = key.trim();
        } catch (error: any) { }
    } else {
        const hardcodedKey = "6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHmKmIYtQbGVffUafn4grxXukjNz+mLI/d2lO8A9DuHxAxu4MmrN4lqhtklB0Pcnfq+AIk+1s6kU0D6JIIxpfQs1FV6pzwrzAWDLexRPvVgpswR/C5n69gFjFmWmtV0bGCTAj6H/MHgwElWYT9nYccmuFH0sfAj7VELa2HehqREJ7di+05AWeWIScfcWsf0XZ3JK+2X3reYvhUEAxrCwX3FDw2p1hPQIfiUVkUd4NRvfwWBLOjP2ZTfp9dzNe1D6T7miK1VBd731o/ex6q1bkdNH+G1KV6OLxdQwb9zWr4AR0QtMKwrphskgAYb5PMCectoVCeRFKtWSm8qnMP+GN8v0GHbJGQ/ZYWMAsE94nCEhnJz9T/xfy1b6WEWqpUUjVsfPzm/naRz0bxVN+AtFW7thTBMwGXoKTJXnKhfrI5u/fRffUu78ejnazBjWCHANN/xF7XOLhnFWw5JZHPXkaoCbiG8kBL3bgL4DtSyZ7jecAPmJUtainwLNMdo75J6hvu/pS+jDUs2tqRjEv6PTehXg";
        try {
            stimulsoftInstance.Base.StiLicense.key = hardcodedKey;
        } catch (fallbackError: any) { }
    }
}

export interface DataType {
    key: React.Key;
    item_code: string;
    item_name: string;
    uom: string;
    location: string;
    picking_quantity: number | string;
    packing_quantity: number | string;
}

export interface PackingListHeader {
    id?: number;
    status: string;
    packing_list_no: string;
    packing_list_date: string;
    sales_order_no?: string;
    delivery_method?: string;
    deliver_to?: string;
    deliver_via?: string;
    date?: string;
    remark?: string;
    same_as_id?: string;
    delivery_address?: string;
    contact_phone?: string;
    created_user?: string;
    timestamp?: string;
    expedition_name?: string;
    eta?: string;
    purchase_order_no_from_so?: string;
    warehouse?: string;
    cs_code?: string;
}

export interface PackingListItem {
    key: React.Key;
    item_code: string;
    item_name: string;
    uom: string;
    location: string;
    picking_quantity: number | string;
    packing_quantity: number | string;
    remark?: string;
    so_no?: string;
    picking_list_no?: string;
    warehouse_code?: string;
    invoice_return_no?: string;
}

export interface PickingListItemApiResponse {
    id: number;
    no_picking: string;
    item_code: string;
    item_name: string;
    uom: string;
    location: string;
    picking_quantity: number | string;
    packing_quantity: number | string;
    warehouse_group?: string;
    return_qty?: number | string;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const columns = () => {
    return [
        { title: "Picking List No.", dataIndex: "picking_list_no", key: "picking_list_no", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Item Code", dataIndex: "item_code", key: "item_code", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Item Name", dataIndex: "item_name", key: "item_name", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "UOM", dataIndex: "uom", key: "uom", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Location", dataIndex: "location", key: "location", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Picking Quantity", dataIndex: "picking_quantity", key: "picking_quantity", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Packing Quantity", dataIndex: "packing_quantity", key: "packing_quantity", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Invoice Return No", dataIndex: "invoice_return_no", key: "invoice_return_no", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) }
    ];
}

export const mapDbStatusToDisplayStatus = (dbStatus: string | number): string => {
    switch (String(dbStatus)) {
        case "1":
            return "Draft";
        case "4":
            return "Close / Complete";
        case "7":
            return "Ready";
        case "5":
            return "Cancel";
        default:
            return String(dbStatus);
    }
};

const mapDisplayStatusToDbStatus = (displayStatus: string): string => {
    switch (displayStatus) {
        case "Draft":
            return "1";
        case "Close / Complete":
            return "4";
        case "Ready":
            return "7";
        case "5":
            return "Cancel";
        default:
            return displayStatus;
    }
};

const usePackingListLogic = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };;

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [form] = Form.useForm<any>();
    const { userData } = useAuth();
    const [packingListItems, setPackingListItems] = useState<PackingListItem[]>([]);
    const packingListItemsRef = useRef<PackingListItem[]>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const initialPackingNoRef = useRef<string | null>(null);
    const isInitialLoad = useRef(true);
    const [isBrowserSoModalVisible, setIsBrowserSoModalVisible] = useState(false);
    const [salesOrderDeliveryAddresses, setSalesOrderDeliveryAddresses] = useState<{ address1: string; address2: string; } | null>(null);
    const [isAddItemModalVisible, setIsAddItemModalVisible] = useState(false);
    const [isEditItemModalVisible, setIsEditItemModalVisible] = useState(false);
    const [editingItemData, setEditingItemData] = useState<PackingListItem | null>(null);
    const location = useLocation();
    const packingListNoFromUrl = (location.state as { packingListNo?: string })?.packingListNo;
    const warehouseFromUrl = (location.state as { warehouse?: string })?.warehouse;
    const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'processing' | 'error' | 'stopped'>('idle');
    const [isPrinting, setIsPrinting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const currentStatus = Form.useWatch('status', form);
    const [isprogress, setIsprogress] = useState(false);
    const isReadyStatus = currentStatus === "Ready";
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);

    const importedPickingListNos = React.useMemo(() => {
        return new Set(packingListItems.map(item => item.picking_list_no).filter(Boolean) as string[]);
    }, [packingListItems]);

    useEffect(() => {
        packingListItemsRef.current = packingListItems;
    }, [packingListItems]);

    const fetcher = (url: string) => axios.get(url).then(res => res.data);
    const { data: expeditionData, error: expeditionError } = useSWR<{ id: number; expedition_name: string; code: string; }[]>(
        `${backendUrl}/api/expeditions`,
        fetcher
    );

    const { data: warehouseData, error: warehouseError } = useSWR<{ warehouse_code: string; warehouse_name: string; }[]>(
        `${backendUrl}/api/warehouses`,
        fetcher
    );

    const warehouseCodeToNameMap = React.useMemo(() => {
        if (warehouseError) {
            console.error("Failed to fetch warehouse data:", warehouseError);
            notification.error({ message: "Gagal memuat data gudang." });
            return new Map<string, string>();
        }
        if (!warehouseData) {
            return new Map<string, string>();
        }
        return new Map(warehouseData.map(wh => [wh.warehouse_code, wh.warehouse_name]));
    }, [warehouseData, warehouseError]);

    const warehouseNameToCodeMap = React.useMemo(() => {
        if (warehouseError) {
            return new Map<string, string>();
        }
        if (!warehouseData) {
            return new Map<string, string>();
        }
        return new Map(warehouseData.map(wh => [wh.warehouse_name, wh.warehouse_code]));
    }, [warehouseData, warehouseError]);

    const expeditionNameOptions = React.useMemo(() => {
        if (expeditionError) {
            console.error("Failed to fetch expedition data:", expeditionError);
            return [{ value: "", label: "--  Empty --" }];
        }
        if (!expeditionData) {
            return [{ value: "", label: "--  Loading --" }];
        }

        return [
            { value: "", label: "--  Empty --" },
            ...expeditionData.map(exp => ({
                value: exp.code,
                label: exp.expedition_name,
            })),
        ];
    }, [expeditionData, expeditionError]);

    const deliveryMethodOptions = [
        { value: "", label: "--  Empty --" },
        { value: "1", label: "Delivery" },
        { value: "2", label: "Self Pickup" },
    ];

    const rowSelection: TableProps<DataType>["rowSelection"] = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
    };

    const getFormattedDateForPackingNo = (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).substring(2);
        return `${month}${year}`;
    };

    const fetchLatestPackingListNo = async (datePart: string): Promise<string | null> => {
        try {
            const response = await axios.get(`${backendUrl}/api/packing-list/latest-no?datePart=${datePart}`);
            return response.data.latestNo;
        } catch (error) {
            console.error("Gagal mengambil nomor Packing List terbaru:", error);
            notification.error({ message: "Gagal mengambil nomor Packing List terbaru dari server." });
            return null;
        }
    };

    const calculateNextPackingListNo = (latestNo: string | null, datePart: string): string => {
        let nextSequence = 1;
        if (latestNo && latestNo.startsWith(`SPPC-AMP${datePart}`)) {
            const existingSequenceStr = latestNo.substring(latestNo.length - 5);
            const existingSequence = parseInt(existingSequenceStr, 10);
            if (!isNaN(existingSequence)) {
                nextSequence = existingSequence + 1;
            }
        }
        const formattedSequence = String(nextSequence).padStart(5, '0');
        return `SPPC-AMP${datePart}${formattedSequence}`;
    };

    const resetToNewForm = useCallback(async () => {
        setIsEditMode(false);
        form.resetFields();
        setPackingListItems([]);
        setSalesOrderDeliveryAddresses(null);

        const today = new Date();
        const formattedDate = dayjs(today).format('DD/MM/YYYY');
        form.setFieldsValue({
            packing_list_date: formattedDate,
            status: "Draft",
            created_user: userData?.nama,
            deliver_via: "",
            deliver_via_code: "",
            cs_code: "",
            warehouse: '',
            packing_list_no: "",
        });

        initialPackingNoRef.current = null;
        isInitialLoad.current = false;
    }, [form, userData]);

    const fetchPackingListData = useCallback(async (packingNo: string) => {
        try {
            const response = await axios.get(`${backendUrl}/api/packing-list-and-items/${encodeURIComponent(packingNo)}`);
            const { header, items } = response.data;

            const headerData = header;
            const itemsData = items.map((item: any) => ({
                ...item,
                key: `${item.item_code}-${Math.random()}`,
                picking_quantity: item.picking_qty,
                packing_quantity: item.packing_qty,
                picking_list_no: item.picking_list_no,
                warehouse_code: item.warehouse_code,
                invoice_return_no: item.invoice_return_no,
            }));

            form.setFieldsValue({
                ...headerData,
                status: mapDbStatusToDisplayStatus(headerData.status),
                packing_list_date: headerData.packing_list_date || '',
                date: headerData.date ? dayjs(headerData.date, 'DD/MM/YYYY') : null,
                same_as_id: headerData.same_as_id === "1",
                deliver_via: headerData.deliver_via_description,
                deliver_via_code: headerData.deliver_via,
                expediton_name: expeditionData?.find(exp => exp.expedition_name === headerData.expedition_name)?.code || headerData.expedition_name || '',
                warehouse: warehouseCodeToNameMap.get(headerData.warehouse) || headerData.warehouse || '',
                cs_code: headerData.cs_code || '',
            });
            setPackingListItems(itemsData);
            setIsEditMode(true);
            initialPackingNoRef.current = packingNo;
        } catch (error) {
            console.error("Gagal memuat data Packing List:", error);
            notification.error({ message: "Gagal memuat data Packing List." });

            resetToNewForm();
        }
    }, [form, resetToNewForm, expeditionData, warehouseCodeToNameMap])

    useEffect(() => {
        if (packingListNoFromUrl) {
            fetchPackingListData(packingListNoFromUrl);
            isInitialLoad.current = false;
        } else if (isInitialLoad.current) {
            resetToNewForm();
        }
    }, [resetToNewForm, packingListNoFromUrl, fetchPackingListData]);

    const autoSaveFormData = useCallback(async (changedValues: any, allValues: any, itemsOverride?: PackingListItem[], forceSave: boolean = false) => {
        if (isInitialLoad.current) {
            return false;
        }

        const currentFormValues = form.getFieldsValue();

        if (!forceSave && currentFormValues.status !== "Draft" && currentFormValues.status !== "1" && changedValues.status !== "Ready") {
            setAutosaveStatus('stopped');
            return false;
        }

        if (!forceSave && isReadyStatus && Object.keys(changedValues).length === 0 && !itemsOverride) {
            return false;
        }

        if (!forceSave && isReadyStatus && changedValues.status !== "Ready" && Object.keys(changedValues).length === 1 && itemsOverride === undefined) {
            return false;
        }


        if (!currentFormValues.sales_order_no) {
            console.warn("Auto-save dibatalkan: Nomor Sales Order belum tersedia.");
            return false;
        }

        if (!currentFormValues.packing_list_no || currentFormValues.packing_list_no === "[Pilih SO untuk generate No]") {
            console.warn("Tidak dapat auto-save: Nomor Packing List belum tersedia.");
            return false;
        }
        setAutosaveStatus('processing');

        try {
            const itemsToUse = itemsOverride || packingListItemsRef.current;
            const warehouseNameFromForm = currentFormValues.warehouse;
            const warehouseCodeToSave = warehouseNameToCodeMap.get(warehouseNameFromForm) || warehouseNameFromForm;
            const dataToAutoSave: PackingListHeader & { packing_list_items: any[] } = {
                status: mapDisplayStatusToDbStatus(currentFormValues.status),
                packing_list_no: currentFormValues.packing_list_no,
                packing_list_date: currentFormValues.packing_list_date,
                sales_order_no: currentFormValues.sales_order_no || '',
                delivery_method: currentFormValues.delivery_method || '',
                deliver_to: currentFormValues.deliver_to || '',
                deliver_via: currentFormValues.deliver_via_code || '',
                date: currentFormValues.date ? dayjs(currentFormValues.date).format('DD/MM/YYYY') : '',
                remark: currentFormValues.remark || '',
                same_as_id: currentFormValues.same_as_id ? "1" : "0",
                delivery_address: currentFormValues.delivery_address || '',
                contact_phone: currentFormValues.contact_phone || '',
                created_user: currentFormValues.created_user || '',
                expedition_name: currentFormValues.expediton_name || '',
                eta: currentFormValues.eta || '',
                warehouse: warehouseCodeToSave,
                cs_code: currentFormValues.cs_code || '',
                packing_list_items: itemsToUse.map(item => ({
                    packing_list_no: currentFormValues.packing_list_no,
                    so_no: currentFormValues.sales_order_no,
                    item_code: item.item_code,
                    item_name: item.item_name,
                    uom: item.uom,
                    location: item.location,
                    picking_qty: String(item.picking_quantity),
                    packing_qty: String(item.packing_quantity),
                    remark: item.remark || '',
                    picking_list_no: item.picking_list_no,
                    warehouse_code: item.warehouse_code,
                    invoice_return_no: item.invoice_return_no || '',
                })),
            };

            console.log("Auto-saving data:", dataToAutoSave);

            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            if (!backendUrl) {
                notification.error({ message: "Backend URL tidak dikonfigurasi." });
                setAutosaveStatus('error');
                return false;
            }
            await axios.post(`${backendUrl}/api/packing-list`, dataToAutoSave);
            if (Object.keys(changedValues).length > 0 || itemsOverride) {
            }
            setAutosaveStatus('idle');
            return true;

        } catch (error: any) {
            console.error("Gagal menyimpan otomatis packing list:", error);
            setAutosaveStatus('error');
            return false;
        }
    }, [form, isEditMode, initialPackingNoRef, isReadyStatus, warehouseNameToCodeMap]);


    const debouncedAutoSave = useCallback(
        debounce((changedValues: any, allValues: any, itemsOverride?: PackingListItem[], forceSave: boolean = false) => {
            autoSaveFormData(changedValues, allValues, itemsOverride, forceSave);
        }, 800),
        [autoSaveFormData]
    );

    const handleBrowserSoClick = useCallback(() => {
        setIsBrowserSoModalVisible(true);
    }, []);


    const handleSelectSalesOrder = useCallback(async (salesOrderNo: string, deliverViaCode: string, deliverViaDescription: string, customerName: string, warehouseGroup: string, csCode: string) => {
        if (!isEditMode) {
            const today = new Date();
            const formattedDateDDMMYY = getFormattedDateForPackingNo(today);
            const latestExistingNo = await fetchLatestPackingListNo(formattedDateDDMMYY);
            const newPackingNo = calculateNextPackingListNo(latestExistingNo, formattedDateDDMMYY);
            form.setFieldsValue({ packing_list_no: newPackingNo });
            initialPackingNoRef.current = newPackingNo;
        }

        form.setFieldsValue({ sales_order_no: salesOrderNo });
        setIsBrowserSoModalVisible(false);
        if (salesOrderNo) {
            form.setFieldsValue({
                deliver_via: deliverViaDescription,
                deliver_via_code: deliverViaCode,
            });
        }
        form.setFieldsValue({ deliver_to: customerName });
        const warehouseName = warehouseCodeToNameMap.get(warehouseGroup) || warehouseGroup;
        form.setFieldsValue({ warehouse: warehouseName });
        form.setFieldsValue({ cs_code: csCode });
        await autoSaveFormData({ sales_order_no: salesOrderNo }, form.getFieldsValue());
    }, [form, warehouseCodeToNameMap, isEditMode, autoSaveFormData]);

    const handleCloseBrowserSoModal = useCallback(() => {
        setIsBrowserSoModalVisible(false);
    }, []);

    const handleAddItemClick = useCallback(() => {
        setIsAddItemModalVisible(true);
    }, []);

    const handleCloseAddItemModal = useCallback(() => {
        setIsAddItemModalVisible(false);
    }, []);

    const handleEditItemClick = useCallback((item: PackingListItem) => {
        setEditingItemData(item);
        setIsEditItemModalVisible(true);
    }, []);

    const handleCloseEditItemModal = useCallback(() => {
        setIsEditItemModalVisible(false);
        setEditingItemData(null);
    }, []);

    const handleSaveEditedItem = useCallback((updatedItem: PackingListItem) => {
        setPackingListItems(prevItems => {
            const newItems = prevItems.map(item =>
                item.key === updatedItem.key ? updatedItem : item
            );
            debouncedAutoSave({}, form.getFieldsValue(), newItems);
            return newItems;
        });
    }, [debouncedAutoSave, form]);

    const handleSelectPickingListItems = useCallback(async (pickingListNos: string[]) => {
        try {
            const allNewItemsToAdd: PackingListItem[] = [];

            const newPickingNos = pickingListNos.filter(no => !importedPickingListNos.has(no));
            if (newPickingNos.length === 0) {
                notification.info({ message: "Tidak ada item baru yang ditambahkan dari Picking List yang dipilih." });
                handleCloseAddItemModal();
                return;
            }
            const response = await axios.get<PickingListItemApiResponse[]>(`${backendUrl}/api/picking-list-items-by-picking-list-no?noPicking=${encodeURIComponent(newPickingNos.join(','))}`);
            if (response.data.length === 0) {
                notification.warning({
                    message: `Tidak Ada Item Valid dari Picking List Terpilih`,
                    description: 'Item tidak dapat diimpor karena data warehouse tidak sesuai dengan master.',
                    duration: 6,
                });
                handleCloseAddItemModal();
                return;
            }
            response.data.forEach(item => {
                const pickQty = parseFloat(String(item.picking_quantity)) || 0;
                const returnQty = parseFloat(String(item.return_qty)) || 0;
                if (pickQty === returnQty) {
                    return;
                }
                const finalQty = pickQty > returnQty ? pickQty - returnQty : pickQty;
                allNewItemsToAdd.push({
                    key: `${item.id}-${item.item_code}-${item.no_picking}`,
                    item_code: item.item_code,
                    item_name: item.item_name,
                    uom: item.uom,
                    location: item.location,
                    picking_quantity: finalQty,
                    packing_quantity: finalQty,
                    remark: '',
                    so_no: form.getFieldValue('sales_order_no'),
                    picking_list_no: item.no_picking,
                    warehouse_code: item.warehouse_group,
                    invoice_return_no: '',
                });
            });

            if (allNewItemsToAdd.length > 0) {
                setPackingListItems(prevItems => {
                    const updatedItems = [...prevItems, ...allNewItemsToAdd];
                    debouncedAutoSave({}, form.getFieldsValue(), updatedItems);
                    return updatedItems;
                });

            } else {
                notification.info({ message: "Tidak ada item baru yang ditambahkan dari Picking List yang dipilih." });
            }
            handleCloseAddItemModal();
        } catch (error) {
            console.error("Gagal memuat item Picking List:", error);
            notification.error({ message: "Gagal memuat item Picking List." });
        }
    }, [debouncedAutoSave, form, handleCloseAddItemModal, packingListItems, importedPickingListNos]);

    const handleDeleteSelectedItems = useCallback(() => {
        if (selectedRowKeys.length === 0) {
            notification.warning({ message: "Pilih setidaknya satu item untuk dihapus." });
            return;
        }

        Modal.confirm({
            title: 'Konfirmasi Hapus',
            content: `Apakah Anda yakin ingin menghapus ${selectedRowKeys.length} item yang dipilih?`,
            okText: 'Ya',
            cancelText: 'Tidak',
            onOk() {
                const updatedItems = packingListItems.filter(item => !selectedRowKeys.includes(item.key));
                setPackingListItems(updatedItems);
                setSelectedRowKeys([]);
                debouncedAutoSave({}, form.getFieldsValue(), updatedItems);
                notification.success({ message: `${selectedRowKeys.length} item berhasil dihapus.` });
            },
            onCancel() {
                console.log('Pembatalan penghapusan');
            },
        });
    }, [selectedRowKeys, packingListItems, debouncedAutoSave, form]);

    const handleRefreshClick = useCallback(async () => {
        const importedPickingNos = Array.from(new Set(
            packingListItems.map(item => item.picking_list_no).filter(Boolean) as string[]
        ));

        if (importedPickingNos.length === 0) {
            notification.warning({ message: "Tidak ada Picking List yang diimpor untuk di-refresh." });
            return;
        }

        setIsprogress(true);
        try {
            const response = await axios.get<PickingListItemApiResponse[]>(
                `${backendUrl}/api/picking-list-items-by-picking-list-no?noPicking=${encodeURIComponent(importedPickingNos.join(','))}`
            );

            const manualItems = packingListItems.filter(item => !item.picking_list_no);

            const refreshedImported: PackingListItem[] = [];
            response.data.forEach(item => {
                const pickQty = parseFloat(String(item.picking_quantity)) || 0;
                const returnQty = parseFloat(String(item.return_qty)) || 0;
                if (pickQty === returnQty) {
                    return;
                }
                const finalQty = pickQty > returnQty ? pickQty - returnQty : pickQty;
                refreshedImported.push({
                    key: `${item.id}-${item.item_code}-${item.no_picking}`,
                    item_code: item.item_code,
                    item_name: item.item_name,
                    uom: item.uom,
                    location: item.location,
                    picking_quantity: finalQty,
                    packing_quantity: finalQty,
                    remark: '',
                    so_no: form.getFieldValue('sales_order_no'),
                    picking_list_no: item.no_picking,
                    warehouse_code: item.warehouse_group,
                    invoice_return_no: '',
                });
            });

            const updatedItems = [...refreshedImported, ...manualItems];
            setPackingListItems(updatedItems);
            const saved = await autoSaveFormData({}, form.getFieldsValue(), updatedItems, true);
            if (saved) {
                notification.success({ message: "Data tabel berhasil di-refresh dari Picking List." });
            } else {
                notification.error({ message: "Refresh tabel berhasil, namun autosave ke database gagal. Periksa status autosave." });
            }
        } catch (error) {
            console.error("Gagal me-refresh data Picking List:", error);
            notification.error({ message: "Gagal me-refresh data Picking List." });
        } finally {
            setIsprogress(false);
        }
    }, [packingListItems, autoSaveFormData, form]);

    const handleSubmitPackingList = useCallback(async () => {
        if (autosaveStatus === 'processing' || isSubmitting || isprogress) return;
        try {
            await form.validateFields();
        } catch (errorInfo) {
            return;
        }
        const packingListNo = form.getFieldValue('packing_list_no');

        if (!packingListNo) {
            notification.error({ message: "Nomor Packing List tidak tersedia untuk disubmit." });
            return;
        }

        const invalidItems = packingListItems.filter(
            item => String(item.packing_quantity) !== String(item.picking_quantity)
        );

        if (invalidItems.length > 0) {
            const errorItemsList = invalidItems.map(item => `Item ${item.item_code} (PL No: ${item.picking_list_no})`).join(', ');
            Modal.error({
                title: 'Validasi Gagal',
                content: `Terdapat item dengan Packing Quantity yang tidak sama dengan Picking Quantity. Silakan periksa item berikut: ${errorItemsList}`,
            });
            return;
        }

        Modal.confirm({
            title: 'Konfirmasi Submit',
            content: `Apakah data sudah benar pada ${packingListNo}?`,
            okText: 'Ya',
            cancelText: 'Tidak',
            onOk: async () => {
                setIsSubmitting(true);
                setIsprogress(true);
                try {
                    debouncedAutoSave.cancel();
                    const uniquePickingListNos = Array.from(new Set(packingListItems.map(item => item.picking_list_no).filter(Boolean)));
                    await axios.post(`${backendUrl}/api/packing-list/update-status`, {
                        packingListNo: packingListNo,
                        status: "7",
                        pickingListNos: uniquePickingListNos
                    });
                    form.setFieldsValue({ status: "Ready" });
                    notification.success({ message: `Packing List No. ${packingListNo} berhasil disubmit.` });
                } catch (error) {
                    console.error('Terjadi kesalahan saat proses submit:', error);
                    notification.error({ message: "Terjadi kesalahan saat proses submit. Silakan coba lagi." });
                    form.setFieldsValue({ status: "Draft" });
                } finally {
                    setIsSubmitting(false);
                    setIsprogress(false);
                }
            },
            onCancel() {
                console.log('Pembatalan submit');
            },
        });
    }, [form, autoSaveFormData, packingListItems, autosaveStatus, isSubmitting])


    const handlePrintClick = useCallback(async () => {
        if (autosaveStatus === 'processing' || isprogress) return;
        const packingListNo = form.getFieldValue('packing_list_no');
        if (!packingListNo) {
            notification.warning({ message: "Nomor Packing List tidak ditemukan untuk dicetak." });
            return;
        }

        setIsPrinting(true);
        try {
            const dataResponse = await axios.get(`${backendUrl}/api/packing-list-and-items/${encodeURIComponent(packingListNo)}`);
            const { header, items } = dataResponse.data;
            if (!header) {
                notification.error({ message: "Data header Packing List tidak ditemukan." });
                return;
            }
            const warehouseNameForPrint = warehouseCodeToNameMap.get(header.warehouse) || header.warehouse || '-';
            const deliveryMethodLabel = deliveryMethodOptions.find(opt => opt.value === header.delivery_method)?.label || '-';
            const reportPayload = {
                Header: [{
                    packing_list_no: header.packing_list_no || "",
                    packing_list_date: header.packing_list_date || "",
                    sales_order_no: header.sales_order_no || "",
                    sales_date: header.sales_date || "",
                    purchase_order_no: header.purchase_order_no_from_so || "",
                    deliver_to: header.deliver_to || "",
                    cs_code: header.cs_code || "",
                    delivery_address: header.delivery_address || "",
                    contact_phone: header.contact_phone || "",
                    delivery_method: deliveryMethodLabel,
                    deliver_via: header.deliver_via_description || "",
                    expedition_name: header.expedition_name || "",
                    delivery_date: header.date || "",
                    eta: header.eta || "",
                    remark: header.remark || "",
                    warehouse: warehouseNameForPrint,
                    created_user: header.created_user || "",
                }],
                Details: items.map((item: any, index: number) => ({
                    no: index + 1,
                    picking_list_no: item.picking_list_no || "",
                    item_code: item.item_code || "",
                    item_name: item.item_name || "",
                    location: item.location || "",
                    picking_qty: parseFloat(item.picking_qty || 0),
                    packing_qty: parseFloat(item.packing_qty || 0),
                    uom: item.uom || "",
                    remark: item.remark || "",
                }))
            };
            await import("stimulsoft-reports-js/Css/stimulsoft.viewer.office2013.whiteblue.css");
            await import("stimulsoft-reports-js/Scripts/stimulsoft.reports");
            const viewerModule = await import("stimulsoft-reports-js/Scripts/stimulsoft.viewer");
            const Stimulsoft = viewerModule.Stimulsoft || (window as any).Stimulsoft;
            StartLicense(Stimulsoft);
            setIsReportModalOpen(true);
            setTimeout(() => {
                const report = Stimulsoft.Report.StiReport.createNewReport();
                report.loadFile("/reports/packing_list/PackingList.mrt");
                const dataSet = new Stimulsoft.System.Data.DataSet("ReportData");
                dataSet.readJson(reportPayload);
                report.regData(dataSet.dataSetName, "", dataSet);
                report.dictionary.synchronize();
                const options = new Stimulsoft.Viewer.StiViewerOptions();
                options.toolbar.showOpenButton = true;
                const viewer = new Stimulsoft.Viewer.StiViewer(options, "StiViewer", false);
                (viewer as any).onOpenReport = (args: any) => {
                    const newReport = args.report;
                    if (newReport) {
                        newReport.regData(dataSet.dataSetName, "", dataSet);
                        newReport.dictionary.synchronize();
                        viewer.report = newReport;
                    }
                };
                viewer.report = report;
                viewer.renderHtml("sti-viewer-container");
            }, 300);
        } catch (error) {
            console.error("Gagal memuat Stimulsoft Report:", error);
            notification.error({ message: "Gagal memuat preview laporan Stimulsoft." });
        } finally {
            setIsPrinting(false);
        }
    }, [form, autosaveStatus, isprogress, warehouseCodeToNameMap, deliveryMethodOptions]);

    const handleReportModalCancel = useCallback(() => {
        setIsReportModalOpen(false);
    }, []);

    const handleNewClick = useCallback(() => {
        if (autosaveStatus === 'processing') return;
        resetToNewForm();
    }, [resetToNewForm, autosaveStatus]);

    const handleVoidPackingList = useCallback(async () => {
        if (autosaveStatus === 'processing' || isprogress) return;
        const packingListNo = form.getFieldValue('packing_list_no');
        if (!packingListNo) {
            notification.warning({ message: "Nomor Packing List tidak ditemukan untuk divoid." });
            return;
        }
        Modal.confirm({
            title: 'Konfirmasi Void',
            content: `Apakah Anda yakin ingin menghapus (void) Packing List ${packingListNo}? Semua data terkait akan dihapus dari database.`,
            okText: 'Ya',
            cancelText: 'Tidak',
            okType: 'danger',
            onOk: async () => {
                setIsprogress(true);
                try {
                    await axios.delete(`${backendUrl}/api/packing-list/${encodeURIComponent(packingListNo)}`);
                    notification.success({ message: `Packing List ${packingListNo} berhasil dihapus.` });
                    resetToNewForm();
                } catch (error) {
                    console.error("Gagal memvoid packing list:", error);
                    notification.error({ message: "Gagal menghapus Packing List dari database." });
                } finally {
                    setIsprogress(false);
                }
            }
        });
    }, [form, resetToNewForm, autosaveStatus]);

    const sameAsIdChecked = Form.useWatch('same_as_id', form);
    const currentSalesOrderNo = Form.useWatch('sales_order_no', form);

    useEffect(() => {
        const fetchSalesOrderDeliveryAddresses = async (soNo: string) => {
            if (!soNo) {
                setSalesOrderDeliveryAddresses(null);
                return;
            }
            try {
                const response = await axios.get(`${backendUrl}/api/sales-order-details?soNo=${encodeURIComponent(soNo)}`);
                const { delivery_address1, delivery_address2 } = response.data;
                setSalesOrderDeliveryAddresses({ address1: delivery_address1 || '', address2: delivery_address2 || '' });
            } catch (error) {
                console.error("Gagal memuat alamat pengiriman Sales Order:", error);
                setSalesOrderDeliveryAddresses(null);
                notification.error({ message: "Gagal memuat alamat pengiriman Sales Order." });
            }
        };

        fetchSalesOrderDeliveryAddresses(currentSalesOrderNo);
    }, [currentSalesOrderNo]);

    useEffect(() => {
        if (sameAsIdChecked) {
            if (salesOrderDeliveryAddresses) {
                const fullAddress = `${salesOrderDeliveryAddresses.address1 || ''} ${salesOrderDeliveryAddresses.address2 || ''}`.trim();
                form.setFieldsValue({ delivery_address: fullAddress });
            } else {

                form.setFieldsValue({ delivery_address: '' });
            }
        } else {

            form.setFieldsValue({ delivery_address: '' });
        }

        debouncedAutoSave({}, form.getFieldsValue());
    }, [sameAsIdChecked, salesOrderDeliveryAddresses, form, debouncedAutoSave]);


    return {
        transformStyle,
        columns: columns(),
        rowSelection,
        form,
        debouncedAutoSave,
        isBrowserSoModalVisible,
        handleBrowserSoClick,
        handleSelectSalesOrder,
        handleCloseBrowserSoModal,
        expeditionNameOptions,
        isAddItemModalVisible,
        handleAddItemClick,
        handleCloseAddItemModal,
        currentSalesOrderNo,
        packingListItems,
        handleSelectPickingListItems,
        handleDeleteSelectedItems,
        handleRefreshClick,
        isEditItemModalVisible,
        editingItemData,
        handleEditItemClick,
        handleCloseEditItemModal,
        handleSaveEditedItem,
        handleSubmitPackingList,
        handleNewClick,
        handleVoidPackingList,
        isReadyStatus,
        isPrinting,
        handlePrintClick,
        importedPickingListNos,
        autosaveStatus,
        isSubmitting,
        isprogress,
        isReportModalOpen,
        handleReportModalCancel
    };
};


export const withPackingListLogic = (WrappedComponent: React.ComponentType<any>) => {
    return () => {
        const {
            transformStyle,
            columns,
            rowSelection,
            form,
            debouncedAutoSave,
            isBrowserSoModalVisible,
            handleBrowserSoClick,
            handleSelectSalesOrder,
            handleCloseBrowserSoModal,
            expeditionNameOptions,
            isAddItemModalVisible,
            handleAddItemClick,
            handleCloseAddItemModal,
            currentSalesOrderNo,
            packingListItems,
            handleSelectPickingListItems,
            handleDeleteSelectedItems,
            handleRefreshClick,
            isEditItemModalVisible,
            editingItemData,
            handleEditItemClick,
            handleCloseEditItemModal,
            handleSaveEditedItem,
            handleSubmitPackingList,
            handleNewClick,
            handleVoidPackingList,
            isReadyStatus,
            handlePrintClick,
            isPrinting,
            importedPickingListNos,
            autosaveStatus,
            isSubmitting,
            isprogress,
            isReportModalOpen,
            handleReportModalCancel
        } = usePackingListLogic();
        return <WrappedComponent
            transformStyle={transformStyle}
            columns={columns}
            rowSelection={rowSelection}
            form={form}
            debouncedAutoSave={debouncedAutoSave}
            isBrowserSoModalVisible={isBrowserSoModalVisible}
            handleBrowserSoClick={handleBrowserSoClick}
            handleSelectSalesOrder={handleSelectSalesOrder}
            handleCloseBrowserSoModal={handleCloseBrowserSoModal}
            expeditionNameOptions={expeditionNameOptions}
            isAddItemModalVisible={isAddItemModalVisible}
            handleAddItemClick={handleAddItemClick}
            handleCloseAddItemModal={handleCloseAddItemModal}
            currentSalesOrderNo={currentSalesOrderNo}
            packingListItems={packingListItems}
            handleSelectPickingListItems={handleSelectPickingListItems}
            handleDeleteSelectedItems={handleDeleteSelectedItems}
            handleRefreshClick={handleRefreshClick}
            isEditItemModalVisible={isEditItemModalVisible}
            editingItemData={editingItemData}
            handleEditItemClick={handleEditItemClick}
            handleCloseEditItemModal={handleCloseEditItemModal}
            handleSaveEditedItem={handleSaveEditedItem}
            handleSubmitPackingList={handleSubmitPackingList}
            handleNewClick={handleNewClick}
            handleVoidPackingList={handleVoidPackingList}
            isReadyStatus={isReadyStatus}
            handlePrintClick={handlePrintClick}
            isPrinting={isPrinting}
            importedPickingListNos={importedPickingListNos}
            autosaveStatus={autosaveStatus}
            isSubmitting={isSubmitting}
            isprogress={isprogress}
            isReportModalOpen={isReportModalOpen}
            handleReportModalCancel={handleReportModalCancel}
        />;
    };
};


export default usePackingListLogic;